/**
 * Trovva — Lead Detail Drawer
 *
 * The Follow Up Boss-style right-side drawer. Click any lead card →
 * this opens with four tabs:
 *   - Overview   editable fields + quick-action bar
 *   - Timeline   unified chronological feed (notes, tasks, status)
 *   - Tasks      lead's pending + completed tasks, quick-add
 *   - Notes      threaded notes with @mentions + author timestamps
 *
 * Pinned header + tab strip. Only the tab body scrolls.
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  X, Phone, MessageSquare, Mail, Edit3, Trash2, UserPlus, Star, StarOff,
  Clock, CheckCircle2, FileText, User, Info, Plus, Send, Loader2, ArrowRight,
  Calendar, DollarSign, Tag as TagIcon, AlertCircle, Briefcase, Sparkles,
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { theme } from '../../lib/theme';
import { computeLeadScore, stalenessOf, daysSinceContact } from '../../lib/leadScoring';
import StageGateModal from './StageGateModal';
import AIFollowUpModal from '../crm/AIFollowUpModal';
import EnrollActionPlanModal from '../crm/EnrollActionPlanModal';
import { missingForStage } from '../../lib/stageGating';

const TABS = [
  { key: 'overview', label: 'Overview', icon: Info },
  { key: 'timeline', label: 'Timeline', icon: Clock },
  { key: 'tasks',    label: 'Tasks',    icon: CheckCircle2 },
  { key: 'notes',    label: 'Notes',    icon: FileText },
];

const LEAD_STATUSES = [
  // Sales funnel (pre-contract) — matches Leads kanban PIPELINE
  { value: 'new',              label: 'New',              color: '#94A3B8' },
  { value: 'contacted',        label: 'Contacted',        color: '#64748B' },
  { value: 'qualified',        label: 'Qualified',        color: theme.colors.info },
  { value: 'active_search',    label: 'Active search',    color: '#0EA5E9' },
  { value: 'offer_submitted',  label: 'Offer submitted',  color: '#3B82F6' },
  { value: 'offer_accepted',   label: 'Offer accepted',   color: '#7C3AED' },
  // Terminal (lead never "closes" — it converts to a deal at Offer Accepted)
  { value: 'lost',             label: 'Lost',             color: theme.colors.danger },
];

const LEAD_TYPES = ['buyer', 'seller', 'renter', 'commercial', 'pm_owner', 'investor'];

export default function LeadDetailDrawer({ leadId, inboxItemId, onClose, onChange, demoMode, showToast }) {
  const [tab, setTab] = useState('overview');
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [inboxProcessing, setInboxProcessing] = useState(false);
  const [inboxDone, setInboxDone] = useState(false); // shows "processed" state
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false); // brief "Saved ✓" indicator
  const [stageGate, setStageGate] = useState(null); // { targetStatus }
  const [showFollowUp, setShowFollowUp] = useState(false);
  const [showEnrollPlan, setShowEnrollPlan] = useState(false);
  const [showScheduleShowing, setShowScheduleShowing] = useState(false);
  const [smsOptedOut, setSmsOptedOut] = useState(false);

  const loadLead = useCallback(async () => {
    if (!leadId) return;
    setLoading(true); setError(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not signed in');
      const r = await fetch(`/api/leads?id=${leadId}`, {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || `Failed (${r.status})`);
      setLead(data.lead);
    } catch (e) {
      setError(e.message);
    } finally { setLoading(false); }
  }, [leadId]);

  useEffect(() => { loadLead(); }, [loadLead]);

  // Check SMS opt-out status
  useEffect(() => {
    if (!lead?.id) { setSmsOptedOut(false); return; }
    (async () => {
      try {
        const { data } = await supabase
          .from('sms_conversations')
          .select('opted_out')
          .eq('contact_id', lead.id)
          .maybeSingle();
        setSmsOptedOut(!!data?.opted_out);
      } catch { setSmsOptedOut(false); }
    })();
  }, [lead?.id]);

  const score = useMemo(() => lead ? computeLeadScore(lead) : null, [lead]);
  const staleness = useMemo(() => lead ? stalenessOf(lead) : null, [lead]);
  const daysSince = useMemo(() => lead ? daysSinceContact(lead) : null, [lead]);

  const updateLead = async (patch) => {
    if (!lead) return;
    // Gate stage-transitioning updates: if user is advancing to a stage that
    // requires extra fields we don't have yet, surface the stage-gate modal.
    if (patch.lead_status && patch.lead_status !== lead.lead_status) {
      const missing = missingForStage({ ...lead, ...patch }, patch.lead_status);
      if (missing.length > 0) {
        setStageGate({ targetStatus: patch.lead_status, originalPatch: patch });
        return;
      }
    }

    setSaving(true); setError(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const r = await fetch(`/api/leads?id=${lead.id}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(patch),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || 'Update failed');
      setLead(data.lead);
      onChange?.(data.lead);
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 2000);
    } catch (e) {
      setError(e.message);
      showToast?.(e.message);
    } finally { setSaving(false); }
  };

  // Commit a patch bypassing the gate (used after the gate modal is satisfied)
  const commitPatch = async (patch) => {
    setSaving(true); setError(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const r = await fetch(`/api/leads?id=${lead.id}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(patch),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || 'Update failed');
      setLead(data.lead);
      onChange?.(data.lead);
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 2000);
    } catch (e) {
      setError(e.message); showToast?.(e.message);
    } finally { setSaving(false); }
  };

  // Manual save — re-sends current lead data to confirm save
  const manualSave = async () => {
    if (!lead || saving) return;
    setSaving(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const r = await fetch(`/api/leads?id=${lead.id}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ updated_at: new Date().toISOString() }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || 'Save failed');
      setLead(data.lead);
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 2000);
    } catch (e) {
      showToast?.(e.message);
    } finally { setSaving(false); }
  };

  const convertToDeal = async () => {
    if (!lead) return;
    if (!window.confirm(`Convert ${lead.first_name} ${lead.last_name} to an active deal?`)) return;
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const r = await fetch(`/api/leads?action=convert_to_deal`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ contact_id: lead.id }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || 'Convert failed');
      showToast?.('Converted to an active deal.');
      loadLead();
      onChange?.();
    } catch (e) {
      showToast?.(e.message);
    }
  };

  const [contactActions, setContactActions] = useState([]); // track soft actions taken

  // Inbox triage action (claim, snooze, pass, etc.)
  const SOFT_ACTIONS = new Set(['contact_call', 'contact_text', 'contact_email']);
  const processInboxAction = async (action, extra = {}) => {
    if (!inboxItemId) return;
    setInboxProcessing(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const r = await fetch('/api/inbox', {
        method: 'POST',
        headers: { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ item_id: inboxItemId, action, ...extra }),
      });
      if (!r.ok) { const d = await r.json(); throw new Error(d.error || 'Action failed'); }

      if (SOFT_ACTIONS.has(action)) {
        // Soft action: log it, keep inbox bar open
        const label = action === 'contact_call' ? 'Called' : action === 'contact_text' ? 'Texted' : 'Emailed';
        setContactActions(prev => [...prev, label]);
        showToast?.(`${label} logged — lead stays in inbox`);
        loadLead(); // refresh contact data (status may have changed to contacted)
      } else {
        // Hard action: item leaves inbox
        setInboxDone(true);
        showToast?.(`Lead ${action === 'claim' ? 'claimed' : action === 'pass' ? 'passed' : action === 'snooze' ? 'snoozed' : 'processed'}`);
        onChange?.();
      }
    } catch (e) {
      showToast?.(e.message);
    } finally { setInboxProcessing(false); }
  };

  if (!leadId) return null;

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 400,
      display: 'flex', justifyContent: 'flex-end',
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        width: '100%', maxWidth: 540, background: theme.colors.card,
        height: '100vh', overflow: 'hidden',
        boxShadow: '-12px 0 40px rgba(0,0,0,0.2)',
        display: 'flex', flexDirection: 'column',
      }}>
        {loading && <Center><Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} /> Loading…</Center>}
        {error && !loading && <Center><AlertCircle size={16} color={theme.colors.danger} /> {error}</Center>}

        {lead && (
          <>
            {/* Header */}
            <DrawerHeader
              lead={lead}
              score={score}
              staleness={staleness}
              daysSince={daysSince}
              saving={saving}
              savedFlash={savedFlash}
              onClose={onClose}
              onPin={() => updateLead({ pinned: !lead.pinned })}
              onConvert={convertToDeal}
              onStatusChange={(status) => updateLead({ lead_status: status })}
              onSave={manualSave}
            />

            {/* Quick actions + AI follow-up */}
            <div style={{ display: 'flex', alignItems: 'center', borderBottom: `1px solid ${theme.colors.border}` }}>
              <div style={{ flex: 1 }}><QuickActions lead={lead} smsOptedOut={smsOptedOut} onScheduleShowing={() => setShowScheduleShowing(true)} /></div>
              <button onClick={() => setShowEnrollPlan(true)} style={{
                display: 'inline-flex', alignItems: 'center', gap: 5,
                padding: '6px 12px',
                borderRadius: 8, border: `1px solid ${theme.colors.primary}`,
                background: `${theme.colors.primary}08`,
                color: theme.colors.primary, fontSize: 11, fontWeight: 700, cursor: 'pointer',
                whiteSpace: 'nowrap',
              }}>
                <ArrowRight size={12} /> Action Plan
              </button>
              <button onClick={() => setShowFollowUp(true)} style={{
                display: 'inline-flex', alignItems: 'center', gap: 5,
                padding: '6px 12px', margin: '0 12px 0 0',
                borderRadius: 8, border: 'none',
                background: `linear-gradient(135deg, ${theme.colors.accent}, ${theme.colors.primary})`,
                color: 'white', fontSize: 11, fontWeight: 700, cursor: 'pointer',
                whiteSpace: 'nowrap',
              }}>
                <Sparkles size={12} /> AI Follow-Up
              </button>
            </div>

            {/* Inbox triage bar (only when opened from Inbox) */}
            {inboxItemId && !inboxDone && (
              <InboxTriageBar
                processing={inboxProcessing}
                onAction={processInboxAction}
                contactActions={contactActions}
              />
            )}
            {inboxItemId && inboxDone && (
              <div style={{
                padding: '10px 18px', background: '#F0FDF4',
                borderBottom: `1px solid ${theme.colors.border}`,
                display: 'flex', alignItems: 'center', gap: 8,
                fontSize: 12, fontWeight: 600, color: '#16A34A',
              }}>
                <CheckCircle2 size={14} /> Inbox item processed
                <button onClick={onClose} style={{
                  marginLeft: 'auto', padding: '4px 10px', borderRadius: 6,
                  border: 'none', background: '#16A34A', color: 'white',
                  fontSize: 11, fontWeight: 600, cursor: 'pointer',
                }}>Close</button>
              </div>
            )}

            {/* Tabs */}
            <div style={{
              display: 'flex', gap: 2, padding: '4px 12px 0',
              borderBottom: `1px solid ${theme.colors.border}`, flexShrink: 0,
            }}>
              {TABS.map(t => {
                const Icon = t.icon;
                const active = tab === t.key;
                return (
                  <button key={t.key} onClick={() => setTab(t.key)} style={{
                    display: 'inline-flex', alignItems: 'center', gap: 5,
                    padding: '8px 10px', border: 'none', background: 'transparent', cursor: 'pointer',
                    borderBottom: active ? `2px solid ${theme.colors.primary}` : '2px solid transparent',
                    color: active ? theme.colors.primary : theme.colors.textLight,
                    fontWeight: active ? 700 : 600, fontSize: 12,
                    marginBottom: -1,
                  }}>
                    <Icon size={13} /> {t.label}
                  </button>
                );
              })}
            </div>

            {/* Body */}
            <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
              {tab === 'overview' && <OverviewTab lead={lead} onUpdate={updateLead} demoMode={demoMode} />}
              {tab === 'timeline' && <TimelineTab contactId={lead.id} />}
              {tab === 'tasks'    && <TasksTab lead={lead} demoMode={demoMode} showToast={showToast} />}
              {tab === 'notes'    && <NotesTab contactId={lead.id} demoMode={demoMode} showToast={showToast} />}
            </div>
          </>
        )}

        <style>{`@keyframes spin { from{transform:rotate(0)} to{transform:rotate(360deg)} } @keyframes fadeIn { from{opacity:0} to{opacity:1} }`}</style>
      </div>

      {/* AI Follow-Up Modal */}
      {showFollowUp && lead && (
        <AIFollowUpModal
          contact={lead}
          onClose={() => setShowFollowUp(false)}
          onSent={() => { setShowFollowUp(false); loadLead(); showToast?.('Follow-up sent!'); }}
        />
      )}

      {/* Action Plan Enrollment Modal */}
      {showEnrollPlan && lead && (
        <EnrollActionPlanModal
          contactId={lead.id}
          contactName={`${lead.first_name || ''} ${lead.last_name || ''}`.trim()}
          onClose={() => setShowEnrollPlan(false)}
          onEnrolled={() => { setShowEnrollPlan(false); loadLead(); showToast?.('Enrolled in action plan!'); }}
          showToast={showToast}
        />
      )}

      {/* Schedule Showing Modal */}
      {showScheduleShowing && lead && (
        <ScheduleShowingModal
          lead={lead}
          onClose={() => setShowScheduleShowing(false)}
          onScheduled={() => { setShowScheduleShowing(false); loadLead(); showToast?.('Showing scheduled!'); }}
          showToast={showToast}
        />
      )}

      {/* Stage gate — opens when advancing the lead to a stage that needs more info */}
      {stageGate && lead && (
        <StageGateModal
          lead={lead}
          targetStatus={stageGate.targetStatus}
          onCancel={() => setStageGate(null)}
          onConfirm={async (patch) => { await commitPatch(patch); setStageGate(null); }}
        />
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────
// Header (pinned)
// ──────────────────────────────────────────────────────────────────
// Pipeline stage order for the stepper
const STAGE_ORDER = ['new', 'contacted', 'qualified', 'active_search', 'offer_submitted', 'offer_accepted'];

function DrawerHeader({ lead, score, staleness, daysSince, saving, savedFlash, onClose, onPin, onConvert, onStatusChange, onSave }) {
  const statusMeta = LEAD_STATUSES.find(s => s.value === lead.lead_status) || LEAD_STATUSES[0];
  const initials = `${lead.first_name?.[0] || ''}${lead.last_name?.[0] || ''}`.toUpperCase();
  const currentIdx = STAGE_ORDER.indexOf(lead.lead_status);
  const isTerminal = lead.lead_status === 'closed' || lead.lead_status === 'lost';

  return (
    <div style={{
      padding: '16px 18px 12px', borderBottom: `1px solid ${theme.colors.border}`, flexShrink: 0,
    }}>
      {/* Top row: Save Changes + Saved indicator + Pin + Close */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 6, marginBottom: 10 }}>
        {/* Auto-save indicator */}
        {savedFlash && (
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            fontSize: 11, fontWeight: 600, color: theme.colors.success,
            animation: 'fadeIn 0.2s ease-in',
          }}>
            <CheckCircle2 size={12} /> Saved
          </span>
        )}
        {saving && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, color: theme.colors.textLight }}>
            <Loader2 size={12} style={{ animation: 'spin 1s linear infinite' }} /> Saving…
          </span>
        )}
        {/* Manual Save button */}
        <button onClick={onSave} disabled={saving} style={{
          display: 'inline-flex', alignItems: 'center', gap: 5,
          padding: '5px 12px', borderRadius: 6,
          border: `1px solid ${theme.colors.border}`,
          background: saving ? '#F1F5F9' : theme.colors.card,
          color: saving ? theme.colors.textLight : theme.colors.text,
          fontSize: 11, fontWeight: 600, cursor: saving ? 'not-allowed' : 'pointer',
        }}
          onMouseEnter={e => { if (!saving) e.currentTarget.style.background = '#F5F7FA'; }}
          onMouseLeave={e => { if (!saving) e.currentTarget.style.background = theme.colors.card; }}
        >
          Save Changes
        </button>
        <button onClick={onPin} title={lead.pinned ? 'Unpin' : 'Pin'} style={iconBtn()}>
          {lead.pinned ? <Star size={15} color={theme.colors.accent} fill={theme.colors.accent} /> : <StarOff size={15} />}
        </button>
        <button onClick={onClose} style={iconBtn()}><X size={16} /></button>
      </div>

      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <div style={{
          width: 46, height: 46, borderRadius: 23,
          background: theme.colors.primary, color: 'white',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 16, fontWeight: 800, flexShrink: 0,
        }}>{initials}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            <h2 style={{ margin: 0, fontSize: 17, color: theme.colors.text, fontWeight: 700 }}>
              {lead.first_name} {lead.last_name}
            </h2>
            {score != null && <ScoreChip score={score} />}
          </div>
          <div style={{ fontSize: 12, color: theme.colors.textLight, marginTop: 3, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <StageBadge statusMeta={statusMeta} />
            {lead.lead_type && <span style={{ textTransform: 'capitalize' }}>{lead.lead_type}</span>}
            {staleness && <StalenessBadge staleness={staleness} days={daysSince} />}
          </div>
        </div>
      </div>

      {/* Update Lead Status — compact 3-col grid */}
      {!isTerminal && (
        <div style={{ marginTop: 10 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: theme.colors.textLight, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 5 }}>
            Update Lead Status
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 4 }}>
            {LEAD_STATUSES.filter(s => s.value !== 'lost').map(s => {
              const isCurrent = s.value === lead.lead_status;
              return (
                <button
                  key={s.value}
                  onClick={() => { if (!isCurrent) onStatusChange(s.value); }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    padding: '6px 8px', borderRadius: 6,
                    border: isCurrent ? `2px solid ${s.color}` : `1px solid ${theme.colors.border}`,
                    background: isCurrent ? `${s.color}10` : theme.colors.card,
                    cursor: isCurrent ? 'default' : 'pointer',
                    transition: 'background 0.15s',
                  }}
                  onMouseEnter={e => { if (!isCurrent) { e.currentTarget.style.background = `${s.color}10`; e.currentTarget.style.borderColor = s.color; } }}
                  onMouseLeave={e => { if (!isCurrent) { e.currentTarget.style.background = theme.colors.card; e.currentTarget.style.borderColor = theme.colors.border; } }}
                >
                  <div style={{
                    width: 14, height: 14, borderRadius: 7, flexShrink: 0,
                    border: isCurrent ? `2px solid ${s.color}` : `2px solid ${theme.colors.border}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    {isCurrent && <div style={{ width: 8, height: 8, borderRadius: 4, background: s.color }} />}
                  </div>
                  <span style={{ fontSize: 11, fontWeight: isCurrent ? 700 : 500, color: isCurrent ? s.color : theme.colors.text }}>
                    {s.label}
                  </span>
                </button>
              );
            })}
          </div>
          {/* Lost — full width */}
          <button
            onClick={() => { if (lead.lead_status !== 'lost') onStatusChange('lost'); }}
            style={{
              marginTop: 4, width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              padding: '6px 8px', borderRadius: 6,
              border: lead.lead_status === 'lost' ? `2px solid ${theme.colors.danger}` : `1px solid ${theme.colors.border}`,
              background: lead.lead_status === 'lost' ? `${theme.colors.danger}10` : theme.colors.card,
              cursor: lead.lead_status === 'lost' ? 'default' : 'pointer',
              fontSize: 11, fontWeight: lead.lead_status === 'lost' ? 700 : 500,
              color: lead.lead_status === 'lost' ? theme.colors.danger : theme.colors.textLight,
              transition: 'background 0.15s',
            }}
            onMouseEnter={e => { if (lead.lead_status !== 'lost') { e.currentTarget.style.background = `${theme.colors.danger}10`; } }}
            onMouseLeave={e => { if (lead.lead_status !== 'lost') { e.currentTarget.style.background = theme.colors.card; } }}
          >
            <X size={12} /> Mark Lost
          </button>

          {/* Convert to deal when offer accepted */}
          {lead.lead_status === 'offer_accepted' && (
            <button onClick={onConvert} style={{
              marginTop: 4, width: '100%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 5,
              padding: '7px 12px', borderRadius: 6, border: 'none',
              background: theme.colors.accent, color: 'white',
              fontSize: 11, fontWeight: 700, cursor: 'pointer',
            }}>
              <Briefcase size={12} /> Convert to Deal
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────
// Quick actions row (call, text, email)
// ──────────────────────────────────────────────────────────────────
function QuickActions({ lead, smsOptedOut, onScheduleShowing }) {
  const canCall  = !!lead.phone;
  const canText  = !!lead.phone && !smsOptedOut;
  const canEmail = !!lead.email;
  return (
    <div style={{
      display: 'flex', gap: 8, padding: '10px 18px',
      borderBottom: `1px solid ${theme.colors.border}`, flexShrink: 0,
      background: '#FAFBFC',
    }}>
      <QuickAction icon={<Phone size={14} />}   label="Call"   disabled={!canCall}  href={canCall ? `tel:${lead.phone}` : undefined} />
      <QuickAction icon={<MessageSquare size={14} />} label={smsOptedOut ? 'Opted out' : 'Text'} disabled={!canText} href={canText ? `sms:${lead.phone}` : undefined} />
      <QuickAction icon={<Mail size={14} />}    label="Email" disabled={!canEmail} href={canEmail ? `mailto:${lead.email}` : undefined} />
      <QuickAction icon={<Calendar size={14} />} label="Showing" onClick={onScheduleShowing} />
    </div>
  );
}
function QuickAction({ icon, label, disabled, href, onClick }) {
  const s = {
    flex: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
    padding: '8px 10px', borderRadius: 8, fontSize: 12, fontWeight: 600,
    textDecoration: 'none', cursor: disabled ? 'not-allowed' : 'pointer',
    background: disabled ? '#F1F5F9' : theme.colors.card,
    color: disabled ? theme.colors.textLight : theme.colors.text,
    border: `1px solid ${theme.colors.border}`,
  };
  if (disabled) return <button style={s} disabled>{icon} {label}</button>;
  if (onClick) return <button style={s} onClick={onClick}>{icon} {label}</button>;
  if (href) return <a href={href} style={s}>{icon} {label}</a>;
  return <button style={s} disabled>{icon} {label}</button>;
}

// ──────────────────────────────────────────────────────────────────
// Schedule Showing Modal
// ──────────────────────────────────────────────────────────────────
function ScheduleShowingModal({ lead, onClose, onScheduled, showToast }) {
  const clientName = `${lead.first_name || ''} ${lead.last_name || ''}`.trim() || 'Client';
  const tomorrow = new Date(Date.now() + 24 * 3600 * 1000);
  const [date, setDate] = useState(tomorrow.toISOString().split('T')[0]);
  const [time, setTime] = useState('10:00');
  const [address, setAddress] = useState(lead.property_address || lead.notes_property || '');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const fieldStyle = {
    width: '100%', padding: '8px 12px', borderRadius: 8, fontSize: 13,
    border: `1px solid ${theme.colors.border}`, background: theme.colors.card,
    color: theme.colors.text, outline: 'none', boxSizing: 'border-box',
    fontFamily: 'inherit',
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!date || !time) { showToast?.('Date and time are required'); return; }
    setSubmitting(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const headers = { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' };
      const showingDate = new Date(`${date}T${time}`);
      const showingLabel = showingDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
        + ' at ' + showingDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });

      // 1. Log to timeline via contact_notes
      await supabase.from('contact_notes').insert({
        contact_id: lead.id,
        org_id: lead.org_id,
        author_id: session.user.id,
        body: `Showing scheduled: ${address || 'TBD address'} on ${showingLabel}${notes ? '\n' + notes : ''}`,
        kind: 'showing_scheduled',
      });

      // 2. Create a task for the showing
      await fetch('/api/tasks', {
        method: 'POST', headers,
        body: JSON.stringify({
          title: `Showing: ${address || clientName} — ${showingLabel}`,
          due_date: date,
          lead_id: lead.id,
          agent_id: lead.assigned_to,
        }),
      });

      // 3. Update contact_attempts + last_contacted
      const attempts = (lead.contact_attempts || 0) + 1;
      await supabase.from('contacts').update({
        contact_attempts: attempts,
        last_contacted: new Date().toISOString(),
      }).eq('id', lead.id);

      // 4. If lead is in "new" or "contacted" status, advance to "showing"
      if (['new', 'uncontacted', 'contacted', 'qualified'].includes(lead.lead_status)) {
        await supabase.from('contacts').update({ lead_status: 'showing' }).eq('id', lead.id);
      }

      onScheduled();
    } catch (err) { showToast?.(err.message); }
    finally { setSubmitting(false); }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)',
      zIndex: 80, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
    }} onClick={onClose}>
      <form onClick={e => e.stopPropagation()} onSubmit={submit} style={{
        width: '100%', maxWidth: 440, background: '#fff', borderRadius: 12,
        padding: 20, display: 'grid', gap: 12,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{
            width: 32, height: 32, borderRadius: 8, flexShrink: 0,
            background: '#8B5CF615', display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Calendar size={16} color="#8B5CF6" />
          </div>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: theme.colors.text }}>Schedule Showing</div>
            <div style={{ fontSize: 12, color: theme.colors.textLight }}>for {clientName}</div>
          </div>
        </div>

        <div>
          <label style={{ fontSize: 11, fontWeight: 600, color: theme.colors.textLight, display: 'block', marginBottom: 4 }}>Property Address</label>
          <input value={address} onChange={e => setAddress(e.target.value)}
            placeholder="123 Main St, Medford NJ 08055" style={fieldStyle} />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: theme.colors.textLight, display: 'block', marginBottom: 4 }}>Date *</label>
            <input type="date" value={date} onChange={e => setDate(e.target.value)} required style={fieldStyle} />
          </div>
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: theme.colors.textLight, display: 'block', marginBottom: 4 }}>Time *</label>
            <input type="time" value={time} onChange={e => setTime(e.target.value)} required style={fieldStyle} />
          </div>
        </div>

        <div>
          <label style={{ fontSize: 11, fontWeight: 600, color: theme.colors.textLight, display: 'block', marginBottom: 4 }}>Notes (optional)</label>
          <textarea value={notes} onChange={e => setNotes(e.target.value)}
            placeholder="Lockbox code, parking instructions, things to highlight..."
            rows={3} style={{ ...fieldStyle, resize: 'vertical', minHeight: 60 }} />
        </div>

        <div style={{ fontSize: 11, color: theme.colors.textLight, padding: '6px 0', lineHeight: 1.5 }}>
          This will log to the lead's timeline, create a task, and count as a contact attempt.
          {['new', 'uncontacted', 'contacted', 'qualified'].includes(lead.lead_status) && (
            <span style={{ color: '#8B5CF6', fontWeight: 600 }}> Lead status will advance to "Showing".</span>
          )}
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button type="button" onClick={onClose} style={{
            padding: '8px 14px', borderRadius: 8, border: `1px solid ${theme.colors.border}`,
            background: '#fff', cursor: 'pointer', fontSize: 13,
          }}>Cancel</button>
          <button type="submit" disabled={submitting} style={{
            padding: '8px 16px', borderRadius: 8, border: 'none',
            background: '#8B5CF6', color: '#fff', fontWeight: 700, fontSize: 13,
            cursor: submitting ? 'wait' : 'pointer', opacity: submitting ? 0.6 : 1,
          }}>
            {submitting ? 'Scheduling...' : 'Schedule Showing'}
          </button>
        </div>
      </form>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────
// Inbox triage bar (shown when drawer opened from Inbox)
// ──────────────────────────────────────────────────────────────────
const SNOOZE_OPTS = [
  { label: '1 hour', ms: 3600000 },
  { label: '4 hours', ms: 14400000 },
  { label: 'Tomorrow 9 AM', ms: null },
  { label: '2 days', ms: 172800000 },
];
const PASS_REASONS = ['junk', 'duplicate', 'wrong_area', 'not_real', 'other'];

function InboxTriageBar({ processing, onAction, contactActions = [] }) {
  const [showSnooze, setShowSnooze] = useState(false);
  const [showPass, setShowPass] = useState(false);

  const getSnoozeUntil = (opt) => {
    if (opt.ms) return new Date(Date.now() + opt.ms).toISOString();
    const d = new Date();
    d.setDate(d.getDate() + 1);
    d.setHours(9, 0, 0, 0);
    return d.toISOString();
  };

  const actionBtn = (label, color, onClick, done) => (
    <button onClick={onClick} disabled={processing} style={{
      flex: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 4,
      padding: '7px 4px', border: done ? `1px solid ${color}` : 'none', borderRadius: 6,
      background: done ? `${color}18` : `${color}0A`, color,
      fontSize: 10, fontWeight: 700, cursor: processing ? 'wait' : 'pointer',
      opacity: processing ? 0.5 : 1,
    }}>{done && <CheckCircle2 size={10} />}{label}</button>
  );

  return (
    <div style={{
      padding: '8px 18px',
      background: '#FFFBEB', borderBottom: `1px solid ${theme.colors.border}`,
      flexShrink: 0,
    }}>
      {/* Log contact attempts (soft — stays in inbox) */}
      <div style={{ fontSize: 10, fontWeight: 700, color: theme.colors.textLight, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>
        Log Contact Attempt
      </div>
      <div style={{ display: 'flex', gap: 4, marginBottom: 8 }}>
        {actionBtn('Called', '#22C55E', () => onAction('contact_call'), contactActions.includes('Called'))}
        {actionBtn('Texted', '#3B82F6', () => onAction('contact_text'), contactActions.includes('Texted'))}
        {actionBtn('Emailed', '#8B5CF6', () => onAction('contact_email'), contactActions.includes('Emailed'))}
      </div>

      {/* Final decision (hard — removes from inbox) */}
      <div style={{ fontSize: 10, fontWeight: 700, color: theme.colors.textLight, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>
        Close Inbox Item
      </div>
      <div style={{ display: 'flex', gap: 4, position: 'relative' }}>
        {actionBtn('Claim', '#16A34A', () => onAction('claim'))}

        {/* Snooze */}
        <div style={{ flex: 1, display: 'flex', position: 'relative' }}>
          {actionBtn('Snooze', '#F97316', () => setShowSnooze(!showSnooze))}
          {showSnooze && (
            <div style={{
              position: 'absolute', top: '100%', left: 0, zIndex: 50, marginTop: 4,
              background: theme.colors.card, border: `1px solid ${theme.colors.border}`,
              borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.15)', minWidth: 140, padding: '4px 0',
            }}>
              {SNOOZE_OPTS.map(opt => (
                <button key={opt.label} onClick={() => { onAction('snooze', { snooze_until: getSnoozeUntil(opt) }); setShowSnooze(false); }} style={{
                  display: 'block', width: '100%', padding: '8px 14px', background: 'transparent',
                  border: 'none', cursor: 'pointer', fontSize: 12, color: theme.colors.text, textAlign: 'left',
                }}
                  onMouseEnter={e => { e.target.style.background = theme.colors.bg; }}
                  onMouseLeave={e => { e.target.style.background = 'transparent'; }}
                >{opt.label}</button>
              ))}
            </div>
          )}
        </div>

        {/* Pass */}
        <div style={{ flex: 1, display: 'flex', position: 'relative' }}>
          {actionBtn('Pass', '#EF4444', () => setShowPass(!showPass))}
          {showPass && (
            <div style={{
              position: 'absolute', top: '100%', right: 0, zIndex: 50, marginTop: 4,
              background: theme.colors.card, border: `1px solid ${theme.colors.border}`,
              borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.15)', minWidth: 140, padding: '4px 0',
            }}>
              {PASS_REASONS.map(reason => (
                <button key={reason} onClick={() => { onAction('pass', { pass_reason: reason }); setShowPass(false); }} style={{
                  display: 'block', width: '100%', padding: '8px 14px', background: 'transparent',
                  border: 'none', cursor: 'pointer', fontSize: 12, color: theme.colors.text, textAlign: 'left', textTransform: 'capitalize',
                }}
                  onMouseEnter={e => { e.target.style.background = theme.colors.bg; }}
                  onMouseLeave={e => { e.target.style.background = 'transparent'; }}
                >{reason.replace(/_/g, ' ')}</button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────
// Overview tab — editable fields
// ──────────────────────────────────────────────────────────────────
function OverviewTab({ lead, onUpdate }) {
  const [editing, setEditing] = useState({});
  const [draft, setDraft] = useState({});

  const startEdit = (field) => {
    setEditing(e => ({ ...e, [field]: true }));
    setDraft(d => ({ ...d, [field]: lead[field] ?? '' }));
  };
  const cancelEdit = (field) => {
    setEditing(e => ({ ...e, [field]: false }));
    setDraft(d => { const next = { ...d }; delete next[field]; return next; });
  };
  const commit = async (field) => {
    const v = draft[field];
    if (v === (lead[field] ?? '')) { cancelEdit(field); return; }
    await onUpdate({ [field]: v === '' ? null : v });
    cancelEdit(field);
  };

  return (
    <div style={{ padding: 18 }}>
      <Section title="Contact">
        <InlineField label="Email" value={lead.email} field="email" editing={editing.email} draft={draft} setDraft={setDraft}
          onStart={startEdit} onCancel={cancelEdit} onCommit={commit} type="email" />
        <InlineField label="Phone" value={lead.phone} field="phone" editing={editing.phone} draft={draft} setDraft={setDraft}
          onStart={startEdit} onCancel={cancelEdit} onCommit={commit} />
        <InlineField label="Location" value={[lead.city, lead.state].filter(Boolean).join(', ') || '—'} readOnly />
      </Section>

      <Section title="Pipeline">
        <SelectField label="Status" field="lead_status" value={lead.lead_status} options={LEAD_STATUSES.map(s => ({ value: s.value, label: s.label }))} onUpdate={onUpdate} />
        <SelectField label="Type" field="lead_type" value={lead.lead_type} options={LEAD_TYPES.map(t => ({ value: t, label: t }))} onUpdate={onUpdate} />
        <InlineField label="Source" value={lead.lead_source} field="lead_source" editing={editing.lead_source} draft={draft} setDraft={setDraft}
          onStart={startEdit} onCancel={cancelEdit} onCommit={commit} />
      </Section>

      <Section title="Specifics">
        <InlineField label="Budget" value={lead.budget} field="budget" editing={editing.budget} draft={draft} setDraft={setDraft}
          onStart={startEdit} onCancel={cancelEdit} onCommit={commit} />
        <InlineField label="Timeline" value={lead.timeline} field="timeline" editing={editing.timeline} draft={draft} setDraft={setDraft}
          onStart={startEdit} onCancel={cancelEdit} onCommit={commit} />
      </Section>

      <Section title="Tags">
        <TagEditor tags={lead.tags || []} onSave={(next) => onUpdate({ tags: next })} />
      </Section>

      <Section title="Scratch notes">
        <textarea
          defaultValue={lead.notes || ''}
          onBlur={(e) => { if ((e.target.value || '') !== (lead.notes || '')) onUpdate({ notes: e.target.value || null }); }}
          placeholder="Private scratchpad — saves on blur. Use Notes tab for structured team-visible notes."
          rows={4}
          style={{ ...fieldInput(), resize: 'vertical', fontFamily: 'inherit' }}
        />
      </Section>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────
// Timeline tab — unified event stream
// ──────────────────────────────────────────────────────────────────
function TimelineTab({ contactId }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      setLoading(true); setError(null);
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const r = await fetch(`/api/lead-timeline?contact_id=${contactId}`, {
          headers: { Authorization: `Bearer ${session.access_token}` },
        });
        const data = await r.json();
        if (!r.ok) throw new Error(data.error || 'Failed');
        setEvents(data.events || []);
      } catch (e) { setError(e.message); }
      finally { setLoading(false); }
    })();
  }, [contactId]);

  if (loading) return <Center><Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> Loading…</Center>;
  if (error) return <Center><AlertCircle size={14} color={theme.colors.danger} /> {error}</Center>;
  if (!events.length) return <div style={{ padding: 32, textAlign: 'center', color: theme.colors.textLight, fontSize: 13 }}>No activity yet.</div>;

  return (
    <div style={{ padding: 18 }}>
      {events.map((e, i) => <TimelineEvent key={i} event={e} />)}
    </div>
  );
}

function TimelineEvent({ event }) {
  const meta = timelineIconFor(event.kind);
  const Icon = meta.icon;
  const date = new Date(event.at);
  const when = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) + ' · '
             + date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  return (
    <div style={{
      display: 'flex', gap: 10, padding: '10px 0',
      borderBottom: `1px solid ${theme.colors.border}`,
    }}>
      <div style={{
        width: 28, height: 28, borderRadius: 14, flexShrink: 0,
        background: `${meta.color}15`, color: meta.color,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}><Icon size={14} /></div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: theme.colors.textLight, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
          {meta.label} · {when}
        </div>
        <div style={{ fontSize: 13, color: theme.colors.text, marginTop: 2, lineHeight: 1.4, whiteSpace: 'pre-wrap' }}>
          {event.body}
        </div>
        {(event.author_name || event.agent_name) && (
          <div style={{ fontSize: 10, color: theme.colors.textLight, marginTop: 2 }}>
            by {event.author_name || event.agent_name}
          </div>
        )}
      </div>
    </div>
  );
}

function timelineIconFor(kind) {
  const map = {
    note:              { icon: FileText,      label: 'Note',             color: theme.colors.info },
    task_created:      { icon: Plus,          label: 'Task created',     color: theme.colors.primary },
    task_completed:    { icon: CheckCircle2,  label: 'Task completed',   color: theme.colors.success },
    lead_created:      { icon: UserPlus,      label: 'Lead added',       color: theme.colors.accent },
    status_marker:     { icon: ArrowRight,    label: 'Status',           color: theme.colors.textLight },
    message:           { icon: MessageSquare, label: 'Message',          color: '#3B82F6' },
    showing_scheduled: { icon: Calendar,      label: 'Showing',          color: '#8B5CF6' },
  };
  return map[kind] || { icon: FileText, label: kind, color: theme.colors.textLight };
}

// ──────────────────────────────────────────────────────────────────
// Tasks tab
// ──────────────────────────────────────────────────────────────────
function TasksTab({ lead, showToast }) {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newTitle, setNewTitle] = useState('');
  const [newDue, setNewDue] = useState(() => new Date(Date.now() + 24 * 3600 * 1000).toISOString().split('T')[0]);
  const [adding, setAdding] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const titleRef = { current: null };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const r = await fetch(`/api/tasks?range=all`, {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      const data = await r.json();
      if (r.ok) setTasks((data.tasks || []).filter(t => t.lead_id === lead.id));
    } catch { /* noop */ } finally { setLoading(false); }
  }, [lead.id]);

  useEffect(() => { load(); }, [load]);

  const addTask = async () => {
    if (!newTitle.trim()) return;
    setAdding(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const r = await fetch('/api/tasks', {
        method: 'POST',
        headers: { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: newTitle.trim(),
          due_date: newDue,
          lead_id: lead.id,
          agent_id: lead.assigned_to,
        }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || 'Failed');
      setNewTitle('');
      setShowForm(false);
      showToast?.('Task added');
      load();
    } catch (e) { showToast?.(e.message); }
    finally { setAdding(false); }
  };

  const toggleTask = async (task) => {
    const newStatus = task.status === 'completed' ? 'pending' : 'completed';
    try {
      const { data: { session } } = await supabase.auth.getSession();
      await fetch(`/api/tasks?id=${task.id}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus, completed_source: 'web' }),
      });
      load();
    } catch { /* noop */ }
  };

  return (
    <div style={{ padding: 18 }}>
      {/* Compact add row — expands on click */}
      {!showForm ? (
        <button onClick={() => { setShowForm(true); setTimeout(() => titleRef.current?.focus(), 50); }} style={{
          display: 'flex', alignItems: 'center', gap: 8, width: '100%',
          padding: '10px 12px', marginBottom: 14, borderRadius: 8,
          border: `1px dashed ${theme.colors.border}`, background: 'transparent',
          color: theme.colors.textLight, fontSize: 13, cursor: 'pointer',
        }}
        onMouseEnter={e => { e.currentTarget.style.background = '#F8FAFC'; e.currentTarget.style.borderColor = theme.colors.primary; }}
        onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = theme.colors.border; }}
        >
          <Plus size={14} /> Add a task…
        </button>
      ) : (
        <div style={{ marginBottom: 14, padding: 12, borderRadius: 8, border: `1px solid ${theme.colors.border}`, background: '#FAFBFC' }}>
          <input
            ref={el => { titleRef.current = el; }}
            value={newTitle}
            onChange={e => setNewTitle(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && newTitle.trim()) addTask(); if (e.key === 'Escape') { setShowForm(false); setNewTitle(''); } }}
            placeholder="What needs to be done?"
            autoFocus
            style={{ ...fieldInput(), width: '100%', marginBottom: 8, fontSize: 13 }}
          />
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <input
              type="date" value={newDue} onChange={e => setNewDue(e.target.value)}
              style={{ ...fieldInput(), flex: 1, fontSize: 12 }}
            />
            <button onClick={() => { if (newTitle.trim()) addTask(); }} disabled={adding || !newTitle.trim()} style={{
              padding: '8px 16px', borderRadius: 8, border: 'none',
              background: theme.colors.primary, color: 'white',
              fontWeight: 600, fontSize: 12, cursor: adding ? 'wait' : 'pointer',
              opacity: (adding || !newTitle.trim()) ? 0.5 : 1,
            }}>
              {adding ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> : 'Add'}
            </button>
            <button onClick={() => { setShowForm(false); setNewTitle(''); }} style={{
              padding: '8px 12px', borderRadius: 8, border: `1px solid ${theme.colors.border}`,
              background: 'transparent', color: theme.colors.textLight, fontSize: 12, cursor: 'pointer',
            }}>Cancel</button>
          </div>
        </div>
      )}

      {loading ? <Center><Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> Loading…</Center>
        : !tasks.length ? <div style={{ padding: 24, textAlign: 'center', color: theme.colors.textLight, fontSize: 13 }}>No tasks for this lead yet.</div>
        : tasks.map(t => <TaskRow key={t.id} task={t} onToggle={() => toggleTask(t)} />)}
    </div>
  );
}
function TaskRow({ task, onToggle }) {
  const done = task.status === 'completed';
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 10,
      padding: '10px 0', borderBottom: `1px solid ${theme.colors.border}`,
      opacity: done ? 0.55 : 1,
    }}>
      <button onClick={onToggle} style={{
        background: 'transparent', border: 'none', cursor: 'pointer',
        color: done ? theme.colors.success : theme.colors.textLight, padding: 0,
      }}>
        {done ? <CheckCircle2 size={18} /> : <span style={{ width: 18, height: 18, borderRadius: 9, border: `1.5px solid ${theme.colors.border}`, display: 'inline-block' }} />}
      </button>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, color: theme.colors.text, textDecoration: done ? 'line-through' : 'none', fontWeight: 600 }}>
          {task.title}
        </div>
        <div style={{ fontSize: 11, color: theme.colors.textLight, marginTop: 2 }}>Due {task.due_date}</div>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────
// Notes tab — with @mentions
// ──────────────────────────────────────────────────────────────────
function NotesTab({ contactId, showToast }) {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState('');
  const [mentions, setMentions] = useState([]);
  const [teammates, setTeammates] = useState([]);
  const [posting, setPosting] = useState(false);
  const [mentionQuery, setMentionQuery] = useState(null); // {start, query} or null

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const r = await fetch(`/api/contact-notes?contact_id=${contactId}`, {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      const data = await r.json();
      if (r.ok) setNotes(data.notes || []);
    } catch { /* noop */ } finally { setLoading(false); }
  }, [contactId]);

  useEffect(() => { load(); }, [load]);

  // Load teammates for @mention autocomplete
  useEffect(() => {
    (async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;
        const { data: me } = await supabase.from('users').select('org_id').eq('id', user.id).maybeSingle();
        if (!me?.org_id) return;
        const { data: users } = await supabase.from('users')
          .select('id, full_name').eq('org_id', me.org_id).order('full_name');
        setTeammates(users || []);
      } catch { /* noop */ }
    })();
  }, []);

  const handleTextChange = (e) => {
    const v = e.target.value;
    setText(v);
    // Detect @mention trigger
    const caret = e.target.selectionStart;
    const before = v.slice(0, caret);
    const m = before.match(/@(\w*)$/);
    if (m) setMentionQuery({ start: caret - m[0].length, query: m[1].toLowerCase() });
    else setMentionQuery(null);
  };

  const filteredTeammates = mentionQuery
    ? teammates.filter(t => (t.full_name || '').toLowerCase().includes(mentionQuery.query))
    : [];

  const pickMention = (user) => {
    if (!mentionQuery) return;
    const before = text.slice(0, mentionQuery.start);
    const after = text.slice(mentionQuery.start + 1 + mentionQuery.query.length);
    const inserted = `@${user.full_name} `;
    setText(before + inserted + after);
    setMentions(ms => (ms.includes(user.id) ? ms : [...ms, user.id]));
    setMentionQuery(null);
  };

  const postNote = async () => {
    if (!text.trim()) return;
    setPosting(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const r = await fetch('/api/contact-notes', {
        method: 'POST',
        headers: { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ contact_id: contactId, body: text.trim(), mentions }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || 'Failed');
      setText(''); setMentions([]);
      load();
    } catch (e) { showToast?.(e.message); }
    finally { setPosting(false); }
  };

  return (
    <div style={{ padding: 18 }}>
      <div style={{ position: 'relative', marginBottom: 14 }}>
        <textarea
          value={text}
          onChange={handleTextChange}
          onKeyDown={e => { if (e.key === 'Enter' && e.metaKey) postNote(); }}
          placeholder="Add a note… use @ to mention a teammate. ⌘+Enter to post."
          rows={3}
          style={{ ...fieldInput(), width: '100%', resize: 'vertical', fontFamily: 'inherit' }}
        />
        {mentionQuery && filteredTeammates.length > 0 && (
          <div style={{
            position: 'absolute', left: 6, bottom: -4, transform: 'translateY(100%)',
            background: theme.colors.card, border: `1px solid ${theme.colors.border}`,
            borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
            maxHeight: 180, overflowY: 'auto', zIndex: 10, minWidth: 200,
          }}>
            {filteredTeammates.slice(0, 5).map(t => (
              <button key={t.id} onClick={() => pickMention(t)} style={{
                display: 'block', width: '100%', textAlign: 'left',
                padding: '6px 10px', border: 'none', background: 'transparent',
                cursor: 'pointer', fontSize: 12,
              }}>
                @{t.full_name}
              </button>
            ))}
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 6 }}>
          <div style={{ fontSize: 11, color: theme.colors.textLight }}>
            {mentions.length ? `${mentions.length} mention${mentions.length > 1 ? 's' : ''}` : 'No mentions'}
          </div>
          <button onClick={postNote} disabled={posting || !text.trim()} style={{
            padding: '8px 14px', borderRadius: 8, border: 'none',
            background: theme.colors.primary, color: 'white', fontSize: 12, fontWeight: 600,
            cursor: posting ? 'wait' : 'pointer',
            opacity: (posting || !text.trim()) ? 0.5 : 1,
            display: 'inline-flex', alignItems: 'center', gap: 5,
          }}>
            {posting ? <Loader2 size={12} style={{ animation: 'spin 1s linear infinite' }} /> : <Send size={12} />} Post note
          </button>
        </div>
      </div>

      {loading ? <Center><Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> Loading…</Center>
        : !notes.length ? <div style={{ padding: 24, textAlign: 'center', color: theme.colors.textLight, fontSize: 13 }}>No notes yet.</div>
        : notes.map(n => <NoteRow key={n.id} note={n} />)}
    </div>
  );
}
function NoteRow({ note }) {
  const when = new Date(note.created_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  return (
    <div style={{
      padding: '10px 12px', marginBottom: 8, borderRadius: 8,
      background: '#F8FAFC', border: `1px solid ${theme.colors.border}`,
    }}>
      <div style={{ fontSize: 11, color: theme.colors.textLight, display: 'flex', gap: 6 }}>
        <User size={11} />
        <strong style={{ color: theme.colors.text, fontWeight: 700 }}>{note.author_name}</strong>
        · {when}
      </div>
      <div style={{ fontSize: 13, color: theme.colors.text, marginTop: 5, whiteSpace: 'pre-wrap' }}>
        {note.body}
      </div>
      {note.mention_names?.length > 0 && (
        <div style={{ fontSize: 10, color: theme.colors.textLight, marginTop: 6 }}>
          Mentions: {note.mention_names.map(n => `@${n}`).join(' ')}
        </div>
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────
// Shared bits
// ──────────────────────────────────────────────────────────────────
function Section({ title, children }) {
  return (
    <div style={{ marginBottom: 18 }}>
      <div style={{
        fontSize: 10, fontWeight: 700, color: theme.colors.textLight,
        textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8,
      }}>{title}</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>{children}</div>
    </div>
  );
}

function InlineField({ label, value, field, readOnly, editing, draft, setDraft, onStart, onCancel, onCommit, type = 'text' }) {
  if (readOnly) {
    return (
      <div style={fieldRow()}>
        <div style={fieldLabel()}>{label}</div>
        <div style={{ flex: 1, color: theme.colors.text, fontSize: 13 }}>{value || '—'}</div>
      </div>
    );
  }
  if (!editing) {
    return (
      <div style={fieldRow()}>
        <div style={fieldLabel()}>{label}</div>
        <button onClick={() => onStart(field)} style={{
          flex: 1, textAlign: 'left', padding: '6px 8px', borderRadius: 6,
          background: 'transparent', border: '1px solid transparent', cursor: 'pointer',
          fontSize: 13, color: theme.colors.text,
        }} onMouseEnter={e => e.currentTarget.style.background = '#F5F7FA'}
           onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
          {value || <span style={{ color: theme.colors.textLight }}>—  click to add</span>}
        </button>
      </div>
    );
  }
  return (
    <div style={fieldRow()}>
      <div style={fieldLabel()}>{label}</div>
      <input
        autoFocus
        type={type}
        value={draft[field] ?? ''}
        onChange={e => setDraft(d => ({ ...d, [field]: e.target.value }))}
        onBlur={() => onCommit(field)}
        onKeyDown={e => { if (e.key === 'Enter') onCommit(field); if (e.key === 'Escape') onCancel(field); }}
        style={{ ...fieldInput(), flex: 1 }}
      />
    </div>
  );
}

function SelectField({ label, field, value, options, onUpdate }) {
  return (
    <div style={fieldRow()}>
      <div style={fieldLabel()}>{label}</div>
      <select value={value || ''} onChange={e => onUpdate({ [field]: e.target.value || null })} style={{ ...fieldInput(), flex: 1 }}>
        <option value="">—</option>
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}

function TagEditor({ tags, onSave }) {
  const [adding, setAdding] = useState(false);
  const [newTag, setNewTag] = useState('');

  const add = () => {
    const t = newTag.trim();
    if (!t) return;
    if (tags.includes(t)) { setNewTag(''); setAdding(false); return; }
    onSave([...tags, t]);
    setNewTag(''); setAdding(false);
  };
  const remove = (t) => onSave(tags.filter(x => x !== t));

  return (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
      {tags.map(t => (
        <span key={t} style={{
          display: 'inline-flex', alignItems: 'center', gap: 3,
          padding: '3px 8px', borderRadius: 10,
          background: `${theme.colors.primary}15`, color: theme.colors.primary,
          fontSize: 11, fontWeight: 600,
        }}>
          {t}
          <button onClick={() => remove(t)} style={{
            background: 'transparent', border: 'none', cursor: 'pointer',
            color: 'inherit', padding: 0, display: 'inline-flex', alignItems: 'center',
          }}><X size={10} /></button>
        </span>
      ))}
      {adding ? (
        <input
          autoFocus value={newTag} onChange={e => setNewTag(e.target.value)}
          onBlur={add} onKeyDown={e => { if (e.key === 'Enter') add(); if (e.key === 'Escape') setAdding(false); }}
          placeholder="tag"
          style={{
            fontSize: 11, padding: '3px 8px', borderRadius: 10,
            border: `1px solid ${theme.colors.border}`, width: 80,
          }}
        />
      ) : (
        <button onClick={() => setAdding(true)} style={{
          padding: '3px 8px', borderRadius: 10, border: `1px dashed ${theme.colors.border}`,
          background: 'transparent', cursor: 'pointer', fontSize: 11, color: theme.colors.textLight,
        }}>+ tag</button>
      )}
    </div>
  );
}

function ScoreChip({ score }) {
  const color = score >= 75 ? theme.colors.success
              : score >= 50 ? theme.colors.warning
              : theme.colors.danger;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 3,
      padding: '1px 7px', borderRadius: 8,
      background: `${color}18`, color, fontSize: 10, fontWeight: 800,
    }}>
      {score}
    </span>
  );
}

function StageBadge({ statusMeta }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', padding: '2px 7px',
      borderRadius: 6, fontSize: 10, fontWeight: 700,
      background: `${statusMeta.color}18`, color: statusMeta.color,
    }}>
      {statusMeta.label}
    </span>
  );
}

function StalenessBadge({ staleness, days }) {
  const meta = {
    fresh: { color: theme.colors.success, label: `Fresh · ${days ?? 0}d` },
    warm:  { color: theme.colors.info,    label: `Warm · ${days ?? 0}d` },
    cool:  { color: theme.colors.warning, label: `Cool · ${days ?? 0}d` },
    cold:  { color: theme.colors.danger,  label: `Cold · ${days ?? '—'}d` },
  }[staleness] || { color: theme.colors.textLight, label: '—' };
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', padding: '2px 7px',
      borderRadius: 6, fontSize: 10, fontWeight: 700,
      background: `${meta.color}18`, color: meta.color,
    }}>
      {meta.label}
    </span>
  );
}

function Center({ children }) {
  return <div style={{ padding: 40, textAlign: 'center', color: theme.colors.textLight, fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>{children}</div>;
}

function fieldRow() {
  return { display: 'flex', alignItems: 'center', gap: 10 };
}
function fieldLabel() {
  return { width: 90, fontSize: 11, fontWeight: 700, color: theme.colors.textLight, textTransform: 'uppercase', letterSpacing: '0.04em', flexShrink: 0 };
}
function fieldInput() {
  return {
    padding: '7px 10px', borderRadius: 8,
    border: `1px solid ${theme.colors.border}`,
    fontSize: 13, color: theme.colors.text, background: theme.colors.card, boxSizing: 'border-box',
  };
}
function iconBtn() {
  return {
    background: 'transparent', border: `1px solid ${theme.colors.border}`,
    color: theme.colors.text, borderRadius: 8, width: 30, height: 30,
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
  };
}
